<template>
  <Dialog
    :model-value="modelValue"
    title="Add New Patient"
    dialog-type="primary"
    dialog-size="small"
    title-variant="medium"
    :header-icon-src="require('@/assets/MyPatient/AddPatient_white.svg')"
    :close-on-overlay="false"
    @update:model-value="onDialogUpdate"
    @close="closeDialog"
  >
    <!-- Body -->
    <div class="add-patient-form">
      <form @submit.prevent="handleSubmit">
        <!-- Name Fields Row -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 form-row">
          <Label label="First Name" :required="true">
            <TextInput
              v-model="formData.firstName"
              size="medium"
              placeholder="Patient's First Name"
              @blur="validateField('firstName')"
            />
            <p v-if="errors.firstName && touched.firstName" class="field-error">{{ errors.firstName }}</p>
          </Label>

          <Label label="Middle Name">
            <TextInput
              v-model="formData.middleName"
              size="medium"
              placeholder="Middle Name"
            />
          </Label>

          <Label label="Last Name" :required="true">
            <TextInput
              v-model="formData.lastName"
              size="medium"
              placeholder="Patient's Last Name"
              @blur="validateField('lastName')"
            />
            <p v-if="errors.lastName && touched.lastName" class="field-error">{{ errors.lastName }}</p>
          </Label>
        </div>

        <!-- Phone Number -->
        <div class="form-row">
          <Label label="Phone Number" :required="true">
            <AreaCodePhoneInput
              v-model="phoneInputModel"
              size="medium"
              @blur="touched.phone = true"
            />
            <p v-if="errors.phone && touched.phone" class="field-error">{{ errors.phone }}</p>
          </Label>
        </div>

        <!-- Email Address -->
        <div class="form-row">
          <Label label="Email Address" :required="true">
            <TextInput
              v-model="formData.email"
              type="email"
              size="medium"
              placeholder="example@email.com"
              @input="onEmailInput"
              @blur="onEmailBlur"
            />
            <p v-if="errors.email" class="field-error">{{ errors.email }}</p>
          </Label>
        </div>

        <!-- Date of Birth and Bio Sex Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 form-row">
          <Label label="Date of Birth">
            <TextInput
              v-model="formData.dob"
              :isDateOfBirth="true"
              size="medium"
              placeholder="MM-DD-YYYY"
              @blur="onDobBlur"
            />
            <p v-if="errors.dob" class="field-error">{{ errors.dob }}</p>
          </Label>

          <Label label="Bio Sex">
            <SingleSelector
              v-model="formData.gender"
              size="medium"
              :options="genderOptions"
              placeholder="Please select"
            />
          </Label>
        </div>
      </form>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="add-patient-footer-leading">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="medium"
          display_name="Cancel"
          :disabled="isLoading"
          @click-button="closeDialog"
        />
      </div>
      <div class="add-patient-footer-actions">
        <ResponsiveButton
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          :display_name="isLoading ? 'Saving...' : 'Save'"
          :disabled="!isFormValid || isLoading"
          @click-button="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { addNewPatientApi } from '@/api/orderTest';
import { notify } from '@/utils/notify';

export default {
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'close', 'save'],
  data() {
    return {
      formData: {
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        gender: '',
        email: '',
        phone: '',
      },
      phoneInputModel: { area_code: '1', phone_number: '' },
      errors: {},
      touched: {
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
        dob: false,
      },
      genderOptions: [
        { label: 'Male', value: 'Male' },
        { label: 'Female', value: 'Female' },
      ],
      isLoading: false,
    };
  },
  computed: {
    isFormValid() {
      const requiredFilled =
        this.formData.firstName.trim() &&
        this.formData.lastName.trim() &&
        this.formData.phone.trim() &&
        this.formData.email.trim();
      const hasErrors = Object.values(this.errors).some(Boolean);
      return requiredFilled && !hasErrors;
    },
  },
  watch: {
    'phoneInputModel.phone_number'(newVal) {
      const digits = this.onlyDigits(newVal).slice(0, 10);
      this.formData.phone = digits ? this.formatPhoneDisplay(digits) : '';
      if (!digits) {
        this.errors.phone = 'Phone number is required';
      } else if (digits.length !== 10) {
        this.errors.phone = 'Phone number must be 10 digits';
      } else {
        delete this.errors.phone;
      }
    },
  },
  methods: {
    closeDialog() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    resetForm() {
      this.formData = {
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        gender: '',
        email: '',
        phone: '',
      };
      this.phoneInputModel = { area_code: '1', phone_number: '' };
      this.errors = {};
      this.touched = {
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
        dob: false,
      };
      this.isLoading = false;
    },
    onDialogUpdate(value) {
      this.$emit('update:modelValue', value);
      if (!value) this.$emit('close');
    },
    validateField(field) {
      this.touched[field] = true;
      if (!this.formData[field]?.trim()) {
        this.errors[field] = `${this.getFieldLabel(field)} is required`;
      } else {
        delete this.errors[field];
      }
    },
    getFieldLabel(field) {
      const labels = {
        firstName: 'First name',
        lastName: 'Last name',
        phone: 'Phone number',
        email: 'Email address',
        dob: 'Date of birth',
      };
      return labels[field] || field;
    },
    // ===== Utils =====
    onlyDigits(str) {
      return (str || '').replace(/\D/g, '');
    },
    formatPhoneDisplay(digits) {
      if (digits.length !== 10) return digits;
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    isValidDateYMD(y, m, d) {
      const year = Number(y),
        month = Number(m),
        day = Number(d);
      if (year < 1900 || year > 2100) return false;
      if (month < 1 || month > 12) return false;
      const daysInMonth = new Date(year, month, 0).getDate();
      return day >= 1 && day <= daysInMonth;
    },

    // ===== Email handlers =====
    onEmailInput() {
      const v = (this.formData.email || '').trim();
      if (!v) {
        this.errors.email = 'Email address is required';
      } else if (!this.isValidEmail(v)) {
        this.errors.email = 'Please enter a valid email address';
      } else {
        delete this.errors.email;
      }
    },
    onEmailBlur() {
      this.touched.email = true;
      this.onEmailInput();
    },

    // ===== DOB handlers =====
    onDobBlur() {
      const digits = this.onlyDigits(this.formData.dob);
      if (!digits) {
        delete this.errors.dob;
        return;
      }
      if (digits.length !== 8) {
        this.errors.dob = 'Date must be 8 digits (MM-DD-YYYY)';
        return;
      }
      const mm = digits.slice(0, 2),
        dd = digits.slice(2, 4),
        yyyy = digits.slice(4, 8);
      if (!this.isValidDateYMD(yyyy, mm, dd)) {
        this.errors.dob = 'Please enter a valid date';
      } else {
        delete this.errors.dob;
        this.formData.dob = `${mm}-${dd}-${yyyy}`;
      }
    },

    // ===== Validation before submit =====
    validateForm() {
      this.errors = {};
      if (!this.formData.firstName.trim()) {
        this.errors.firstName = 'First name is required';
      }
      if (!this.formData.lastName.trim()) {
        this.errors.lastName = 'Last name is required';
      }
      const phoneDigits = this.onlyDigits(this.formData.phone);
      if (!phoneDigits) {
        this.errors.phone = 'Phone number is required';
      } else if (phoneDigits.length !== 10) {
        this.errors.phone = 'Phone number must be 10 digits';
      }
      const email = (this.formData.email || '').trim();
      if (!email) {
        this.errors.email = 'Email address is required';
      } else if (!this.isValidEmail(email)) {
        this.errors.email = 'Please enter a valid email address';
      }
      const dobDigits = this.onlyDigits(this.formData.dob);
      if (dobDigits) {
        if (dobDigits.length !== 8) {
          this.errors.dob = 'Date must be 8 digits (MMDDYYYY)';
        } else {
          const mm = dobDigits.slice(0, 2),
            dd = dobDigits.slice(2, 4),
            yyyy = dobDigits.slice(4, 8);
          if (!this.isValidDateYMD(yyyy, mm, dd)) {
            this.errors.dob = 'Please enter a valid date';
          }
        }
      }
      return Object.keys(this.errors).length === 0;
    },

    // ===== Submit =====
    async handleSubmit() {
      if (!this.validateForm()) return;
      try {
        this.isLoading = true;
        const phoneDigits = this.onlyDigits(this.formData.phone);
        const phoneForSave = `+1-${phoneDigits}`;
        let dobForSave = '';
        const dobDigits = this.onlyDigits(this.formData.dob);
        if (dobDigits && dobDigits.length === 8) {
          const mm = dobDigits.slice(0, 2),
            dd = dobDigits.slice(2, 4),
            yyyy = dobDigits.slice(4, 8);
          dobForSave = `${yyyy}-${mm}-${dd}`;
        }
        const { data } = await addNewPatientApi({
          patient_first_name: this.formData.firstName,
          patient_middle_name: this.formData.middleName || '',
          patient_last_name: this.formData.lastName,
          patient_birthdate: this.formData.dob || '',
          patient_email: this.formData.email || '',
          patient_gender: this.formData.gender || '',
          patient_height: this.formData.height || '0',
          patient_weight: this.formData.weight || '0',
          patient_phone: this.formData.phone || '',
          patient_street_address: this.formData.address || '',
          patient_city: this.formData.city || '',
          patient_state: this.formData.state || '',
          patient_zipcode: this.formData.zipcode || '',
          patient_country: this.formData.country || null,
          patient_language: this.formData.language || 'English',
        });
        notify({
          title: 'Success',
          message: 'Patient created successfully!',
          type: 'success',
        });
        this.$emit('save', {
          ...this.formData,
          phone: phoneForSave,
          dob: dobForSave,
          patientId: data.toString(),
        });
        this.resetForm();
        this.closeDialog();
      } catch (error) {
        this.isLoading = false;
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
.add-patient-form {
  width: 100%;
  padding: 8px;
}

.form-row {
  margin-bottom: 16px;
}

.field-error {
  margin-top: 4px;
  font-size: 12px;
  color: var(--ErrorRed, #d10000);
}

.add-patient-footer-leading {
  display: flex;
  align-items: center;
}

.add-patient-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}
</style>
